import { useState, useRef, useEffect, useMemo } from "react";
import { Send, Loader2, RotateCcw, Eye, Flame, Clock, Users, ArrowDown, BookOpen, ChevronDown, ChevronRight, Wrench, ShoppingCart, Handshake, AlertTriangle, Heart, Map } from "lucide-react";
import axios from "axios";
import { trackEvent } from "../../lib/track";
import SharedConstellationMap from "../common/SharedConstellationMap";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const OC = { care: "#34d399", utility: "#60a5fa", creative: "#a78bfa", extraction: "#f87171" };

const ANIM = `
@keyframes glowPulse { 0%,100%{opacity:.5} 50%{opacity:1} }
@keyframes fadeWords { from{opacity:0;filter:blur(3px)} to{opacity:1;filter:blur(0)} }
@keyframes scaleIn { from{opacity:0;transform:scale(.6)} to{opacity:1;transform:scale(1)} }
@keyframes drawStroke { from{stroke-dashoffset:214} to{stroke-dashoffset:var(--target)} }
@keyframes shimmer { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
@keyframes pulseGlow { 0%,100%{box-shadow:0 0 0 rgba(245,158,11,0)} 50%{box-shadow:0 0 40px rgba(245,158,11,.12)} }
@keyframes typeIn { from{width:0} to{width:100%} }
@keyframes revealUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
@keyframes revealLeft { from{opacity:0;transform:translateX(-20px)} to{opacity:1;transform:translateX(0)} }
@keyframes revealRight { from{opacity:0;transform:translateX(20px)} to{opacity:1;transform:translateX(0)} }
`;

/* Animated counter hook */
const useCounter = (target, duration = 1500, active = false) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active || !target) return;
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setVal(target); clearInterval(timer); }
      else setVal(Math.round(start));
    }, 16);
    return () => clearInterval(timer);
  }, [target, active, duration]);
  return val;
};

/* Word-by-word reveal text */
const RevealText = ({ text, delay = 0, className = "" }) => {
  const words = (text || "").split(" ");
  return (
    <span className={className}>
      {words.map((w, i) => (
        <span key={`word-${w}-${i}`} style={{ display: "inline-block", animation: `fadeWords 0.4s ease-out ${delay + i * 0.06}s both` }}>
          {w}&nbsp;
        </span>
      ))}
    </span>
  );
};

/* Animated gauge circle */
const AnimGauge = ({ value, color, active }) => {
  const v = useCounter(value, 1200, active);
  const circumference = 214;
  const offset = circumference - (v / 100) * circumference;
  return (
    <div className="relative w-24 h-24 mx-auto mb-3 flex items-center justify-center">
      <svg width="96" height="96" viewBox="0 0 96 96">
        <circle cx="48" cy="48" r="34" fill="none" stroke="rgba(255,255,255,0.03)" strokeWidth="7" />
        <circle cx="48" cy="48" r="34" fill="none" stroke={color} strokeWidth="7"
          strokeDasharray={circumference}
          strokeDashoffset={active ? offset : circumference}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 1.2s ease-out", filter: `drop-shadow(0 0 8px ${color}60)` }} />
      </svg>
      <span className="absolute text-3xl font-black tabular-nums" style={{ color, textShadow: `0 0 16px ${color}40` }}>{v}</span>
    </div>
  );
};

/* ─── Map PE data → SharedConstellationMap format ─── */
const buildConstellationData = (result) => {
  const software = result.existing_software || [];
  const hardware = result.existing_hardware || [];
  const research = result.existing_research || [];
  const possibilities = result.possibilities || [];

  const steps = [
    { name: "Software AI", apps: software.slice(0, 6).map(s => ({ ...s, entity_type: "application" })) },
    { name: "Hardware AI", apps: hardware.slice(0, 6).map(h => ({ ...h, entity_type: "device" })) },
  ];

  // Add Research ring if papers exist
  if (research.length > 0) {
    steps.push({
      name: "Research",
      apps: research.slice(0, 6).map(r => ({
        product_name: r.paper_title,
        value_orientation: r.value_orientation || "utility",
        tagline: r.abstract ? r.abstract.slice(0, 100) + "..." : `${r.year || ""} · ${r.citations || 0} citations`,
        country: r.country || "",
        entity_type: "research",
      })),
    });
  }

  steps.push({
    name: "Convergence",
    apps: possibilities.slice(0, 4).map(p => ({
      product_name: p.title,
      value_orientation: p.care_or_extraction || "care",
      tagline: p.gap_description || p.description || "",
      country: "",
      entity_type: "application",
    })),
  });

  if (software.length > 3 && hardware.length > 3) {
    const careSw = software.filter(s => s.value_orientation === "care").slice(0, 4);
    const careHw = hardware.filter(h => h.value_orientation === "care").slice(0, 4);
    if (careSw.length > 0) steps.push({ name: "Care AI", apps: careSw.map(s => ({ ...s, entity_type: "application" })) });
    if (careHw.length > 0) steps.push({ name: "Embodied Care", apps: careHw.map(h => ({ ...h, entity_type: "device" })) });
  }

  // Determine dominant orientation
  const allApps = steps.flatMap(s => s.apps);
  const counts = {};
  allApps.forEach(a => { counts[a.value_orientation] = (counts[a.value_orientation] || 0) + 1; });
  const dominant = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || "utility";

  return {
    steps,
    idea_orientation: dominant,
    idea_orientation_reason: "Software + Hardware + Research convergence mapping",
    total_apps_matched: allApps.length,
  };
};

/* Memoized constellation wrapper — prevents data object recreation on parent re-renders */
const MemoizedConstellation = ({ result, activeStep, setActiveStep }) => {
  const data = useMemo(() => buildConstellationData(result), [result]);
  return <SharedConstellationMap data={data} activeStep={activeStep} setActiveStep={setActiveStep} showInsightCards={true} />;
};

/* ========= ROADMAP MODULE — Self-guided expandable integration roadmap ========= */
const ACTION_ICONS = { build: Wrench, procure: ShoppingCart, partner: Handshake };
const ACTION_COLORS = { build: "#f59e0b", procure: "#34d399", partner: "#60a5fa" };

const RoadmapModule = ({ idea, result }) => {
  const [expanded, setExpanded] = useState(false);
  const [roadmap, setRoadmap] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isExtraction = useMemo(() => {
    const orientations = (result?.possibilities || []).map(p => p.care_or_extraction);
    return orientations.includes("extraction");
  }, [result]);

  const fetchRoadmap = async () => {
    if (roadmap) { setExpanded(!expanded); return; }
    setExpanded(true);
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/possibility/roadmap`, {
        idea,
        existing_software: result?.existing_software || [],
        existing_hardware: result?.existing_hardware || [],
        existing_research: result?.existing_research || [],
        possibilities: result?.possibilities || [],
        is_extraction: isExtraction,
      });
      setRoadmap(res.data);
      trackEvent("roadmap_generated", { idea, is_extraction: isExtraction });
    } catch (e) {
      setError("Failed to generate roadmap. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ animation: "revealUp 0.6s ease-out both" }}>
      {/* Toggle Button */}
      <button
        onClick={fetchRoadmap}
        className="w-full group"
        data-testid="roadmap-toggle"
      >
        <div className="flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-amber-500/[0.06] via-violet-500/[0.06] to-emerald-500/[0.06] border border-white/[0.08] rounded-2xl hover:border-white/[0.15] transition-all">
          <Map className="w-5 h-5 text-amber-400" />
          <span className="text-sm font-bold text-slate-200 uppercase tracking-widest">Integration Roadmap</span>
          <span className="text-[10px] text-slate-400 ml-1">— How to build this dream</span>
          {loading ? (
            <Loader2 className="w-4 h-4 text-amber-400 animate-spin ml-auto" />
          ) : expanded ? (
            <ChevronDown className="w-4 h-4 text-slate-400 ml-auto group-hover:text-amber-400 transition-colors" />
          ) : (
            <ChevronRight className="w-4 h-4 text-slate-400 ml-auto group-hover:text-amber-400 transition-colors" />
          )}
        </div>
      </button>

      {/* Loading State */}
      {loading && (
        <div className="mt-4 flex items-center justify-center gap-3 py-10 bg-white/[0.02] border border-white/[0.04] rounded-2xl">
          <Loader2 className="w-5 h-5 text-amber-400 animate-spin" />
          <p className="text-xs text-slate-400">{isExtraction ? "Generating humanitarian alternative roadmap..." : "Mapping integration pathways..."}</p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mt-4 p-4 bg-red-500/[0.06] border border-red-500/20 rounded-xl">
          <p className="text-xs text-red-400">{error}</p>
        </div>
      )}

      {/* Roadmap Content */}
      {expanded && roadmap && !loading && (
        <div className="mt-4 space-y-5" style={{ animation: "revealUp 0.5s ease-out both" }}>

          {/* Humanitarian Redirect Banner */}
          {roadmap.humanitarian_redirect && (
            <div className="bg-gradient-to-r from-rose-500/[0.08] to-emerald-500/[0.08] border border-rose-500/20 rounded-2xl p-6" 
              style={{ animation: "revealUp 0.5s ease-out 0.1s both" }} data-testid="humanitarian-redirect">
              <div className="flex items-start gap-3 mb-4">
                <AlertTriangle className="w-5 h-5 text-rose-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs font-bold text-rose-400 uppercase tracking-wider">Extraction Use Case Detected</p>
                  <p className="text-[10px] text-slate-400 mt-1">{roadmap.humanitarian_redirect.original_concern}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Heart className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs font-bold text-emerald-400 uppercase tracking-wider mb-1">Humanitarian Alternative</p>
                  <p className="text-sm text-slate-200 leading-relaxed">{roadmap.humanitarian_redirect.alternative_vision}</p>
                  <p className="text-[10px] text-emerald-400/70 mt-2 italic">{roadmap.humanitarian_redirect.why_better}</p>
                </div>
              </div>
            </div>
          )}

          {/* Feasibility Score */}
          <div className="flex items-center gap-5 px-5 py-4 bg-white/[0.02] border border-white/[0.06] rounded-2xl"
            style={{ animation: "revealUp 0.5s ease-out 0.15s both" }} data-testid="roadmap-feasibility">
            <div className="relative w-16 h-16 flex items-center justify-center flex-shrink-0">
              <svg width="64" height="64" viewBox="0 0 64 64">
                <circle cx="32" cy="32" r="28" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="4" />
                <circle cx="32" cy="32" r="28" fill="none" 
                  stroke={roadmap.feasibility_score >= 70 ? "#34d399" : roadmap.feasibility_score >= 40 ? "#f59e0b" : "#f87171"} 
                  strokeWidth="4"
                  strokeDasharray={`${roadmap.feasibility_score * 1.76} ${176 - roadmap.feasibility_score * 1.76}`}
                  strokeDashoffset="44" strokeLinecap="round" />
              </svg>
              <span className="absolute text-lg font-black" 
                style={{ color: roadmap.feasibility_score >= 70 ? "#34d399" : roadmap.feasibility_score >= 40 ? "#f59e0b" : "#f87171" }}>
                {roadmap.feasibility_score}
              </span>
            </div>
            <div>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Feasibility Score</p>
              <p className="text-xs text-slate-300 mt-1 leading-relaxed">{roadmap.feasibility_reason}</p>
              {roadmap.timeline_summary && (
                <p className="text-[10px] text-amber-400/70 mt-1">Timeline: {roadmap.timeline_summary}</p>
              )}
            </div>
          </div>

          {/* Steps */}
          {roadmap.steps?.length > 0 && (
            <div className="relative" data-testid="roadmap-steps">
              {/* Vertical connector line */}
              <div className="absolute left-[23px] top-10 bottom-10 w-px bg-gradient-to-b from-amber-500/30 via-violet-500/30 to-emerald-500/30" />

              {roadmap.steps.map((step, i) => {
                const ActionIcon = ACTION_ICONS[step.action_type] || Wrench;
                const actionColor = ACTION_COLORS[step.action_type] || "#a78bfa";
                return (
                  <div key={step.step_number || `step-${i}`} className="relative flex gap-4 mb-4 last:mb-0"
                    style={{ animation: `revealUp 0.4s ease-out ${0.2 + i * 0.15}s both` }}>
                    {/* Step circle */}
                    <div className="relative z-10 flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center border-2"
                      style={{ borderColor: actionColor, background: `${actionColor}12`, boxShadow: `0 0 20px ${actionColor}15` }}>
                      <span className="text-sm font-black" style={{ color: actionColor }}>{step.step_number}</span>
                    </div>

                    {/* Step content */}
                    <div className="flex-1 bg-white/[0.02] border border-white/[0.06] rounded-xl p-4 hover:border-white/[0.12] transition-all">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <p className="text-sm font-bold text-slate-200">{step.title}</p>
                        <div className="flex items-center gap-1.5 px-2 py-1 rounded-full text-[9px] font-bold uppercase tracking-wider flex-shrink-0"
                          style={{ background: `${actionColor}15`, color: actionColor, border: `1px solid ${actionColor}30` }}>
                          <ActionIcon className="w-3 h-3" />
                          {step.action_type}
                        </div>
                      </div>
                      <p className="text-xs text-slate-400 leading-relaxed">{step.description}</p>

                      {/* Entities involved */}
                      {step.entities_involved?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2.5">
                          {step.entities_involved.map((e, j) => (
                            <span key={j} className="text-[9px] px-2 py-0.5 bg-white/[0.04] border border-white/[0.08] rounded-full text-slate-300">
                              {e}
                            </span>
                          ))}
                        </div>
                      )}

                      <div className="flex items-center gap-4 mt-2.5 text-[10px] text-slate-500">
                        {step.duration && <span>Duration: {step.duration}</span>}
                        {step.output && <span className="text-slate-400">Produces: {step.output}</span>}
                      </div>

                      {step.research_citation && (
                        <p className="text-[9px] text-cyan-400/60 mt-1.5 flex items-center gap-1">
                          <BookOpen className="w-3 h-3" />
                          Research: {step.research_citation}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Gaps */}
          {roadmap.gaps?.length > 0 && (
            <div className="bg-amber-500/[0.04] border border-amber-500/10 rounded-2xl p-5"
              style={{ animation: `revealUp 0.4s ease-out ${0.2 + (roadmap.steps?.length || 0) * 0.15}s both` }}
              data-testid="roadmap-gaps">
              <p className="text-[10px] font-bold text-amber-400 uppercase tracking-wider mb-3">Gaps to Bridge</p>
              {roadmap.gaps.map((g, i) => (
                <div key={g.gap || `gap-${i}`} className="mb-3 last:mb-0 pl-3 border-l-2 border-amber-500/20">
                  <p className="text-xs font-semibold text-slate-300">{g.gap}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">{g.mitigation}</p>
                  {g.expected_resolution && <p className="text-[9px] text-amber-400/50 mt-0.5">Expected: {g.expected_resolution}</p>}
                </div>
              ))}
            </div>
          )}

          {/* Impact Statement */}
          {roadmap.impact_statement && (
            <div className="text-center py-4" style={{ animation: `revealUp 0.5s ease-out ${0.3 + (roadmap.steps?.length || 0) * 0.15}s both` }}>
              <p className="text-sm text-slate-200 italic max-w-lg mx-auto leading-relaxed">"{roadmap.impact_statement}"</p>
            </div>
          )}
        </div>
      )}

      {expanded && roadmap && !loading && (
        <div className="text-center py-3" style={{ animation: "revealUp 0.4s ease-out 1.5s both" }}>
          <ArrowDown className="w-5 h-5 text-violet-400/40 mx-auto animate-bounce" style={{ animationDuration: "2s" }} />
        </div>
      )}
    </div>
  );
};

export const PossibilityEngine = () => {
  const [idea, setIdea] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [stage, setStage] = useState(0);
  const [loadPhase, setLoadPhase] = useState(0);
  const [activeStep, setActiveStep] = useState(null);
  const inputRef = useRef(null);
  const stageRefs = useRef([]);

  const LOAD_PHASES = [
    { text: "Dreaming...", sub: "Imagining what could be", color: "#f59e0b" },
    { text: "Envisioning...", sub: "Scanning the AI universe", color: "#a78bfa" },
    { text: "Visualizing...", sub: "Mapping connections and possibilities", color: "#34d399" },
    { text: "Materializing...", sub: "Crystallizing the future", color: "#60a5fa" },
  ];
  useEffect(() => {
    if (!loading) { setLoadPhase(0); return; }
    let i = 0;
    const timer = setInterval(() => { i = Math.min(i + 1, LOAD_PHASES.length - 1); setLoadPhase(i); }, 3000);
    return () => clearInterval(timer);
  }, [loading]);

  useEffect(() => {
    if (!result) { setStage(0); return; }
    const delays = [200, 1400, 3000, 5000, 6500, 8000, 9500];
    const timers = delays.map((d, i) =>
      setTimeout(() => {
        setStage(i + 1);
        requestAnimationFrame(() => {
          const el = stageRefs.current[i];
          if (el) {
            const rect = el.getBoundingClientRect();
            if (rect.top > window.innerHeight * 0.6)
              el.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        });
      }, d)
    );
    return () => timers.forEach(clearTimeout);
  }, [result]);

  const handleSubmit = async () => {
    if (!idea.trim() || loading) return;
    setLoading(true); setError(null); setResult(null); setStage(0); setActiveStep(null);
    trackEvent("possibility_query", "possibility_engine", idea.trim().slice(0, 100));
    try {
      const res = await axios.post(`${API}/possibility`, { idea: idea.trim() }, { timeout: 90000 });
      if (res.data.error) setError(res.data.error);
      else setResult(res.data);
    } catch { setError("Failed to generate. Please try again."); }
    finally { setLoading(false); }
  };

  const conv = result?.convergence;

  return (
    <div className="max-w-4xl mx-auto pb-16 space-y-8" data-testid="possibility-engine">
      <style>{ANIM}</style>

      {/* HERO */}
      <div className="relative text-center pt-8 pb-6 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full"
            style={{ background: "radial-gradient(circle, rgba(245,158,11,0.06) 0%, rgba(139,92,246,0.03) 40%, transparent 70%)" }} />
        </div>
        <div className="relative">
          <h2 className="text-3xl font-black tracking-tight text-slate-100 leading-tight">Possibility Engine</h2>
          <p className="mt-3 text-sm text-amber-400/80 font-semibold max-w-lg mx-auto leading-relaxed">
            What becomes possible for humanity when AI starts<br className="hidden sm:block" /> thinking, feeling, becoming, and breathing into the real world?
          </p>
          <p className="mt-2 text-xs text-slate-400 max-w-md mx-auto">
            Describe a problem or dream. We'll map what exists, what is emerging, and when worlds converge.
          </p>
        </div>
      </div>

      {/* INPUT */}
      <div className="max-w-2xl mx-auto">
        <div className="relative group">
          <div className="absolute -inset-0.5 rounded-2xl bg-gradient-to-r from-amber-500/20 via-violet-500/10 to-emerald-500/20 opacity-0 group-focus-within:opacity-100 transition-opacity blur-sm" />
          <textarea ref={inputRef} value={idea} onChange={e => setIdea(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSubmit(); } }}
            placeholder="e.g. Help elderly people live independently at home..."
            rows={1}
            className="relative w-full px-5 py-4 bg-black/50 border border-white/[0.08] rounded-2xl text-sm resize-none focus:outline-none focus:border-amber-500/30 placeholder:text-slate-500 text-slate-100 pr-36"
            data-testid="possibility-input" />
          <div className="absolute top-1/2 -translate-y-1/2 right-3 flex items-center gap-1.5 z-10">
            {result && <button onClick={() => { setIdea(""); setResult(null); setActiveStep(null); inputRef.current?.focus(); }} className="p-2 rounded-xl hover:bg-white/[0.08] text-slate-400"><RotateCcw className="w-4 h-4" /></button>}
            <button onClick={handleSubmit} disabled={!idea.trim() || loading}
              className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 disabled:opacity-30 text-white text-sm font-bold rounded-xl transition-all shadow-lg shadow-amber-500/25"
              data-testid="possibility-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {loading ? LOAD_PHASES[loadPhase]?.text || "Dreaming..." : "Explore"}
            </button>
          </div>
        </div>
        {!result && !loading && (
          <div className="flex flex-wrap justify-center gap-2 mt-5">
            {["Help elderly people live independently", "Make farming sustainable in Africa", "Assist surgeons in remote areas"].map((ex, i) => (
              <button key={ex} onClick={() => setIdea(ex)}
                style={{ animation: `revealUp 0.4s ease-out ${0.1 + i * 0.1}s both` }}
                className="text-xs px-4 py-2 bg-white/[0.02] border border-white/[0.06] rounded-full hover:border-amber-500/25 hover:bg-amber-500/[0.05] text-slate-400 hover:text-amber-300 transition-all">
                {ex}
              </button>
            ))}
          </div>
        )}
      </div>

      {error && <div className="max-w-2xl mx-auto text-center text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl py-3">{error}</div>}

      {/* LOADING */}
      {loading && (
        <div className="text-center py-16" style={{ animation: "revealUp 0.5s ease-out both" }}>
          <div className="relative w-28 h-28 mx-auto mb-6">
            <div className="absolute inset-0 rounded-full border" style={{ borderColor: `${LOAD_PHASES[loadPhase].color}20`, animation: "glowPulse 2s ease-in-out infinite" }} />
            <div className="absolute inset-4 rounded-full border" style={{ borderColor: `${LOAD_PHASES[Math.max(0, loadPhase - 1)].color}15`, animation: "glowPulse 2.5s ease-in-out 0.3s infinite" }} />
            <div className="absolute inset-8 rounded-full border" style={{ borderColor: `${LOAD_PHASES[Math.min(3, loadPhase + 1)].color}10`, animation: "glowPulse 3s ease-in-out 0.6s infinite" }} />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-4 h-4 rounded-full shadow-[0_0_24px]" style={{ background: `linear-gradient(135deg, ${LOAD_PHASES[loadPhase].color}, ${LOAD_PHASES[Math.min(3, loadPhase + 1)].color})`, boxShadow: `0 0 24px ${LOAD_PHASES[loadPhase].color}80`, animation: "glowPulse 1.5s ease-in-out infinite", transition: "all 1s ease" }} />
            </div>
          </div>
          <p className="text-base font-bold transition-all duration-700" style={{ color: LOAD_PHASES[loadPhase].color }}>{LOAD_PHASES[loadPhase].text}</p>
          <p className="text-xs text-slate-400 mt-1 transition-all duration-700">{LOAD_PHASES[loadPhase].sub}</p>
          <div className="flex items-center justify-center gap-2 mt-4">
            {LOAD_PHASES.map((phase, i) => (
              <div key={`phase-${phase.text}`} className="w-1.5 h-1.5 rounded-full transition-all duration-500"
                style={{ background: i <= loadPhase ? LOAD_PHASES[i].color : "rgba(255,255,255,0.06)", boxShadow: i === loadPhase ? `0 0 8px ${LOAD_PHASES[i].color}` : "none" }} />
            ))}
          </div>
        </div>
      )}

      {/* RESULTS */}
      {result && (
        <div className="space-y-2">

          {/* REVEAL 1: Summary */}
          {stage >= 1 && (
            <div ref={el => stageRefs.current[0] = el} className="text-center pb-4" style={{ animation: "revealUp 0.8s ease-out both" }}>
              {result.idea_summary && (
                <p className="text-base text-slate-200 max-w-xl mx-auto leading-relaxed italic">
                  <RevealText text={`"${result.idea_summary}"`} delay={0.2} />
                </p>
              )}
            </div>
          )}

          {/* REVEAL 2: What Already Exists */}
          {stage >= 2 && (
            <div ref={el => stageRefs.current[1] = el}>
              <div className="text-center mb-5" style={{ animation: "scaleIn 0.5s ease-out both" }}>
                <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-500/10 border border-emerald-500/20 rounded-full"
                  style={{ animation: "pulseGlow 2s ease-in-out 0.5s 1" }}>
                  <Eye className="w-4 h-4 text-emerald-400" />
                  <span className="text-xs font-bold text-emerald-400 uppercase tracking-widest">What Already Exists</span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                <div className="bg-gradient-to-b from-emerald-500/[0.06] to-transparent border border-emerald-500/10 rounded-2xl p-5"
                  style={{ animation: "revealLeft 0.6s ease-out 0.2s both" }} data-testid="existing-software">
                  <p className="text-xs font-bold text-emerald-400 uppercase tracking-wider mb-3">AI That Thinks</p>
                  <p className="text-[10px] text-slate-500 mb-3">Software intelligence already in the world</p>
                  {result.existing_software?.length > 0 ? result.existing_software.map((a, i) => (
                    <div key={a.product_name} className="flex items-start gap-2.5 py-2 group"
                      style={{ animation: `revealLeft 0.4s ease-out ${0.4 + i * 0.12}s both` }}>
                      <span className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0 shadow-[0_0_6px_currentColor]" style={{ background: OC[a.value_orientation] || "#64748b", color: OC[a.value_orientation] || "#64748b" }} />
                      <div className="flex-1">
                        <span className="text-xs font-semibold text-slate-200 group-hover:text-emerald-300 transition-colors">{a.product_name}</span>
                        {a.tagline && <p className="text-[10px] text-slate-400 mt-0.5 leading-relaxed">{a.tagline}</p>}
                      </div>
                    </div>
                  )) : <p className="text-xs text-slate-500 italic" style={{ animation: "fadeWords 0.5s ease-out 0.5s both" }}>No specific matches found</p>}
                </div>

                <div className="bg-gradient-to-b from-blue-500/[0.06] to-transparent border border-blue-500/10 rounded-2xl p-5"
                  style={{ animation: "revealRight 0.6s ease-out 0.3s both" }} data-testid="existing-hardware">
                  <p className="text-xs font-bold text-blue-400 uppercase tracking-wider mb-3">AI That Moves</p>
                  <p className="text-[10px] text-slate-500 mb-3">Physical AI already in the world</p>
                  {result.existing_hardware?.length > 0 ? result.existing_hardware.map((d, i) => (
                    <div key={d.product_name} className="flex items-start gap-2.5 py-2 group"
                      style={{ animation: `revealRight 0.4s ease-out ${0.5 + i * 0.12}s both` }}>
                      <span className="w-2 h-2 rounded-sm mt-1.5 flex-shrink-0 shadow-[0_0_6px_currentColor]" style={{ background: OC[d.value_orientation] || "#64748b", color: OC[d.value_orientation] || "#64748b" }} />
                      <div className="flex-1">
                        <span className="text-xs font-semibold text-slate-200 group-hover:text-blue-300 transition-colors">{d.product_name}</span>
                        <span className="text-[10px] text-slate-500 ml-1.5 px-1.5 py-0.5 bg-white/[0.03] rounded">{d.form_factor}</span>
                        {d.tagline && <p className="text-[10px] text-slate-400 mt-0.5 leading-relaxed">{d.tagline}</p>}
                      </div>
                    </div>
                  )) : <p className="text-xs text-slate-500 italic" style={{ animation: "fadeWords 0.5s ease-out 0.6s both" }}>No specific matches found</p>}
                </div>

                {/* Research Papers Column */}
                <div className="bg-gradient-to-b from-cyan-500/[0.06] to-transparent border border-cyan-500/10 rounded-2xl p-5"
                  style={{ animation: "revealUp 0.6s ease-out 0.4s both" }} data-testid="existing-research">
                  <div className="flex items-center gap-2 mb-3">
                    <BookOpen className="w-3.5 h-3.5 text-cyan-400" />
                    <p className="text-xs font-bold text-cyan-400 uppercase tracking-wider">AI That Knows</p>
                  </div>
                  <p className="text-[10px] text-slate-500 mb-3">Research advancing the frontier</p>
                  {result.existing_research?.length > 0 ? result.existing_research.map((r, i) => (
                    <div key={r.paper_title} className="flex items-start gap-2.5 py-2 group"
                      style={{ animation: `revealUp 0.4s ease-out ${0.5 + i * 0.12}s both` }}>
                      <span className="w-2 h-2 rounded-[2px] mt-1.5 flex-shrink-0 shadow-[0_0_6px_currentColor] rotate-45" style={{ background: OC[r.value_orientation] || "#64748b", color: OC[r.value_orientation] || "#64748b" }} />
                      <div className="flex-1 min-w-0">
                        <span className="text-xs font-semibold text-slate-200 group-hover:text-cyan-300 transition-colors line-clamp-2">{r.paper_title}</span>
                        <div className="flex items-center gap-2 mt-0.5">
                          {r.year && <span className="text-[9px] text-slate-500">{r.year}</span>}
                          {r.citations > 0 && <span className="text-[9px] text-cyan-400/60">{r.citations} cites</span>}
                          {r.institution && r.institution !== "Unknown" && <span className="text-[9px] text-slate-500 truncate">{r.institution}</span>}
                        </div>
                      </div>
                    </div>
                  )) : <p className="text-xs text-slate-500 italic" style={{ animation: "fadeWords 0.5s ease-out 0.7s both" }}>No research papers matched</p>}
                </div>
              </div>

              <div className="text-center py-3" style={{ animation: "revealUp 0.4s ease-out 1s both" }}>
                <ArrowDown className="w-5 h-5 text-amber-400/40 mx-auto animate-bounce" style={{ animationDuration: "2s" }} />
              </div>
            </div>
          )}

          {/* REVEAL 3: What Is Becoming */}
          {stage >= 3 && (
            <div ref={el => stageRefs.current[2] = el}>
              <div className="text-center mb-5" style={{ animation: "scaleIn 0.5s ease-out both" }}>
                <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-500/10 border border-amber-500/20 rounded-full"
                  style={{ animation: "pulseGlow 2s ease-in-out 0.5s 1" }}>
                  <Flame className="w-4 h-4 text-amber-400" />
                  <span className="text-xs font-bold text-amber-400 uppercase tracking-widest">What Is Becoming</span>
                </div>
                <p className="text-[10px] text-slate-400 mt-2" style={{ animation: "fadeWords 0.5s ease-out 0.3s both" }}>When thinking meets doing, new realities emerge</p>
              </div>

              <div className="space-y-4 mb-4">
                {result.possibilities?.map((p, i) => (
                  <div key={p.possibility || `poss-${i}`}
                    className="relative bg-gradient-to-r from-amber-500/[0.04] to-transparent border border-amber-500/10 rounded-2xl p-6 hover:border-amber-500/25 transition-all group overflow-hidden"
                    data-testid={`possibility-${i}`}
                    style={{ animation: `revealUp 0.6s ease-out ${i * 0.25}s both` }}>
                    <div className="absolute inset-0 pointer-events-none"
                      style={{ background: "linear-gradient(90deg, transparent, rgba(245,158,11,0.04), transparent)", backgroundSize: "200% 100%", animation: `shimmer 1.5s ease-in-out ${i * 0.25}s 1` }} />
                    <div className="relative">
                      <div className="flex items-start gap-3 mb-4">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg"
                          style={{ background: `linear-gradient(135deg, ${OC[p.care_or_extraction] || "#f59e0b"}30, ${OC[p.care_or_extraction] || "#f59e0b"}10)`, border: `1px solid ${OC[p.care_or_extraction] || "#f59e0b"}30`, animation: `scaleIn 0.4s ease-out ${0.1 + i * 0.25}s both` }}>
                          <span className="text-lg">{i === 0 ? "\u2726" : i === 1 ? "\u25C8" : "\u25C7"}</span>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-base font-bold text-slate-100">{p.title}</h3>
                          <p className="text-sm text-slate-400 mt-1.5 leading-relaxed"><RevealText text={p.description} delay={0.2 + i * 0.25} /></p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {[
                          { label: "The Thinking", val: p.brain, color: "emerald" },
                          { label: "The Doing", val: p.body, color: "blue" },
                          { label: "Who It Serves", val: p.who_benefits, color: "violet", icon: <Users className="w-2.5 h-2.5" /> },
                        ].map((col, ci) => (
                          <div key={ci} className={`bg-${col.color}-500/[0.06] border border-${col.color}-500/10 rounded-xl p-3`}
                            style={{ animation: `revealUp 0.4s ease-out ${0.4 + i * 0.25 + ci * 0.1}s both` }}>
                            <p className={`text-[10px] text-${col.color}-400 font-bold uppercase tracking-wider flex items-center gap-1`}>
                              {col.icon}{col.label}
                            </p>
                            <p className="text-xs text-slate-300 mt-1 leading-relaxed">{col.val}</p>
                          </div>
                        ))}
                      </div>
                      {p.early_signals && p.early_signals !== "N/A" && (
                        <p className="text-[10px] text-amber-400/50 mt-3" style={{ animation: `fadeWords 0.5s ease-out ${0.8 + i * 0.25}s both` }}>Early signals: {p.early_signals}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="text-center py-3" style={{ animation: "revealUp 0.4s ease-out 1s both" }}>
                <ArrowDown className="w-5 h-5 text-violet-400/40 mx-auto animate-bounce" style={{ animationDuration: "2s" }} />
              </div>
            </div>
          )}

          {/* REVEAL 4: Ecosystem Constellation — NOW USES SHARED COMPONENT */}
          {stage >= 4 && (
            <div ref={el => stageRefs.current[3] = el}>
              <div className="text-center mb-5" style={{ animation: "scaleIn 0.5s ease-out both" }}>
                <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-cyan-500/10 border border-cyan-500/20 rounded-full"
                  style={{ animation: "pulseGlow 2s ease-in-out 0.5s 1" }}>
                  <span className="text-sm">{"\u2726"}</span>
                  <span className="text-xs font-bold text-cyan-400 uppercase tracking-widest">The Ecosystem Constellation</span>
                </div>
                <p className="text-[10px] text-slate-400 mt-2" style={{ animation: "fadeWords 0.5s ease-out 0.3s both" }}>How thinking, doing, and becoming connect</p>
              </div>

              <MemoizedConstellation result={result} activeStep={activeStep} setActiveStep={setActiveStep} />

              <div className="text-center py-3" style={{ animation: "revealUp 0.4s ease-out 1.5s both" }}>
                <ArrowDown className="w-5 h-5 text-violet-400/40 mx-auto animate-bounce" style={{ animationDuration: "2s" }} />
              </div>
            </div>
          )}

          {/* REVEAL 5: When Worlds Converge */}
          {stage >= 5 && conv && (
            <div ref={el => stageRefs.current[4] = el}>
              <div className="text-center mb-5" style={{ animation: "scaleIn 0.5s ease-out both" }}>
                <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-violet-500/10 border border-violet-500/20 rounded-full"
                  style={{ animation: "pulseGlow 2s ease-in-out 0.5s 1" }}>
                  <Clock className="w-4 h-4 text-violet-400" />
                  <span className="text-xs font-bold text-violet-400 uppercase tracking-widest">When Worlds Converge</span>
                </div>
                <p className="text-[10px] text-slate-400 mt-2" style={{ animation: "fadeWords 0.5s ease-out 0.3s both" }}>The timeline of thinking meeting reality</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {[
                  { label: "Thinking Maturity", val: conv.software_maturity, reason: conv.software_maturity_reason, color: "#34d399" },
                  { label: "Doing Readiness", val: conv.hardware_readiness, reason: conv.hardware_readiness_reason, color: "#60a5fa" },
                  { label: "Convergence", val: conv.convergence_score, reason: conv.gap_description, color: "#a78bfa" },
                ].map((g, i) => (
                  <div key={g.label} className="bg-gradient-to-b from-white/[0.04] to-transparent border border-white/[0.06] rounded-2xl p-5 text-center"
                    style={{ animation: `revealUp 0.6s ease-out ${i * 0.2}s both` }}>
                    <p className="text-[10px] font-bold uppercase tracking-wider mb-4" style={{ color: g.color }}>{g.label}</p>
                    <AnimGauge value={g.val} color={g.color} active={stage >= 4} />
                    <p className="text-[10px] text-slate-400 leading-relaxed mt-2"><RevealText text={g.reason} delay={0.5 + i * 0.2} /></p>
                  </div>
                ))}
              </div>

              <div className="bg-gradient-to-r from-amber-500/[0.03] via-violet-500/[0.03] to-emerald-500/[0.03] border border-white/[0.06] rounded-2xl p-6"
                style={{ animation: "revealUp 0.6s ease-out 0.6s both" }}>
                <p className="text-xs font-bold text-slate-200 mb-5 tracking-wider uppercase">The Road Ahead</p>
                <div className="relative">
                  <div className="absolute top-6 left-[15%] right-[15%] h-6 -translate-y-1/2 pointer-events-none rounded-full"
                    style={{ background: "linear-gradient(90deg, rgba(245,158,11,0.06), rgba(52,211,153,0.06), rgba(167,139,250,0.06))", filter: "blur(10px)" }} />
                  <div className="relative flex items-start justify-between">
                    {[
                      { label: "Early Prototypes", year: conv.timeline?.early_prototypes, color: "#f59e0b", desc: "First glimpses" },
                      { label: "Commercially Viable", year: conv.timeline?.commercial_availability, color: "#34d399", desc: "Available to all" },
                      { label: "Mass Adoption", year: conv.timeline?.mass_adoption, color: "#a78bfa", desc: "Part of daily life" },
                    ].map((phase, i) => (
                      <div key={phase.label} className="flex-1 text-center" style={{ animation: `scaleIn 0.5s ease-out 0.8s both` }}>
                        <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center border-2"
                          style={{ borderColor: phase.color, background: `${phase.color}12`, boxShadow: `0 0 24px ${phase.color}20, 0 0 48px ${phase.color}08` }}>
                          <span className="text-sm font-black" style={{ color: phase.color }}>{i + 1}</span>
                        </div>
                        <p className="text-base font-black" style={{ color: phase.color }}>{phase.year}</p>
                        <p className="text-[10px] font-semibold text-slate-300 mt-0.5">{phase.label}</p>
                        <p className="text-[10px] text-slate-500">{phase.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
                {conv.timeline_reasoning && (
                  <div className="mt-6 border-t border-white/[0.04] pt-4" style={{ animation: "revealUp 0.5s ease-out 1.5s both" }}>
                    <p className="text-xs text-slate-400 leading-relaxed"><RevealText text={conv.timeline_reasoning} delay={1.6} /></p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* REVEAL 6: Integration Roadmap — Self-Guided Module */}
          {stage >= 6 && result && (
            <RoadmapModule idea={result.idea_summary || idea} result={result} />
          )}

          {/* REVEAL 7: Provocation */}
          {stage >= 7 && result.provocation && (
            <div ref={el => stageRefs.current[5] = el} className="relative text-center py-10 mt-6" style={{ animation: "revealUp 1s ease-out both" }}>
              <div className="absolute inset-0 pointer-events-none"
                style={{ background: "radial-gradient(ellipse at center, rgba(245,158,11,0.08), transparent 60%)", animation: "glowPulse 3s ease-in-out infinite" }} />
              <p className="relative text-xl font-bold text-slate-100 max-w-lg mx-auto leading-relaxed italic">
                <RevealText text={`"${result.provocation}"`} delay={0.3} />
              </p>
            </div>
          )}

          {/* Footer */}
          {stage >= 7 && (
            <div className="flex items-center justify-between text-[10px] text-slate-500 pt-4" style={{ animation: "revealUp 0.5s ease-out 0.5s both" }}>
              <span>Mapped across {result.total_software?.toLocaleString()} apps + {result.total_hardware?.toLocaleString()} devices + {result.total_research?.toLocaleString() || 0} research papers</span>
              <span>&copy; {new Date().getFullYear()} AI Orientation Observatory</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PossibilityEngine;
